import { toJS } from 'mobx';
import moment from 'moment';
import { IReportRow, REPORTS_BODY_KEY, REPORT_TYPE } from '../types/reports';
import { SORTING_DIRECTION } from 'src/constants/main';
import { arraySort, sumArray } from 'src/utils/helpers';
import { COMPARED_PERIOD } from 'src/view/screens/ReportsScreen/screens/SalesPeriod/types/salesPeriod';
import { dateFormatForRequest, defaultDateFormat } from '~/constants/dateTime';
import { IResponseReportBody } from '~/types/reports';

export const sumTotal = (report: IReportRow[], key: REPORTS_BODY_KEY): number => {
  return Math.round(sumArray(report.map((row) => row.body[key] || 0)));
};

export const calcAverageCheckValue = (report: IReportRow[]) => {
  const revenueTotal = sumTotal(report, REPORTS_BODY_KEY.REVENUE);
  const ordersTotal = sumTotal(report, REPORTS_BODY_KEY.ORDERS_COUNT);
  return ordersTotal ? revenueTotal / ordersTotal : 0;
};

export const sortReport = (report: IReportRow[], sortField?: REPORTS_BODY_KEY, sortDir?: SORTING_DIRECTION, noSorting?: boolean) => {
  const _report = toJS(report);

  if (sortField) {
    return _report.sort((row1, row2) => {
      const [a, b] = [row1.body[sortField] || 0, row2.body[sortField] || 0];

      return sortDir === SORTING_DIRECTION.ASCENDING ? a - b : b - a;
    });
  }

  if (noSorting) {
    return sortDir === SORTING_DIRECTION.ASCENDING ? _report : _report.reverse();
  } else {
    return sortDir === SORTING_DIRECTION.ASCENDING ? arraySort(_report, 'name') : arraySort(_report, 'name').reverse();
  }
};

export const getReportTotal = (report: IReportRow[], reportType?: REPORT_TYPE): IResponseReportBody => {
  return {
    orders_count: sumTotal(report, REPORTS_BODY_KEY.ORDERS_COUNT),
    revenue: sumTotal(report, REPORTS_BODY_KEY.REVENUE),
    average_check: calcAverageCheckValue(report),
    pickup_count: sumTotal(report, REPORTS_BODY_KEY.PICKUP_COUNT),
    pickup_revenue: sumTotal(report, REPORTS_BODY_KEY.PICKUP_REVENUE),
    delivery_count: sumTotal(report, REPORTS_BODY_KEY.DELIVERY_COUNT),
    delivery_revenue: sumTotal(report, REPORTS_BODY_KEY.DELIVERY_REVENUE),
    dinein_count: sumTotal(report, REPORTS_BODY_KEY.DINEIN_COUNT),
    dinein_revenue: sumTotal(report, REPORTS_BODY_KEY.DINEIN_REVENUE),
    ...(reportType === REPORT_TYPE.COMBO ? { combo_price: sumTotal(report, REPORTS_BODY_KEY.TOTAL_COMBO_PRICE) } : {}),
  };
};

export const calcPercentageDiff = (actualValue: number, comparedValue: number): number => {
  return Math.round(((actualValue - comparedValue) / comparedValue) * 100);
};

export const subtractDatePeriod = (
  startDate: string | number,
  endDate: string | number,
  comparedPeriod: COMPARED_PERIOD,
  format: string = dateFormatForRequest
) => {
  const unit = comparedPeriod === COMPARED_PERIOD.YEAR ? 'years' : 'months';
  startDate = moment(startDate).subtract(1, unit).format(format);
  endDate = moment(endDate).subtract(1, unit).format(format);
  return [startDate, endDate];
};

export const getComparedDatePeriod = (startDate: number, endDate: number, comparedPeriod: COMPARED_PERIOD, onlyYears?: boolean) => {
  const format: string = onlyYears ? 'YYYY' : defaultDateFormat;
  return subtractDatePeriod(startDate * 1000, endDate * 1000, comparedPeriod, format);
};
